import React, { Fragment, useState } from "react";
import { Container, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import classNames from "classnames";

import { topBarLinks } from "./TopBar";

import logo from "../assets/images/WisconsinBaseballTournaments_Full_Logo_Resize-1.png";
import { authActions } from "../features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import useBreakpoint from "../hooks/useBreakpoint";
import { AUTH_LEVELS } from "../lib/globals";
import { isXOrSmaller, validateToken } from "../lib/utils";
import { INavLinkType } from "../types/shared";

const Header = (): React.JSX.Element => {
    const [show, setShow] = useState<{ [key: string]: boolean }>({});
    const [navBarExpanded, setNavBarExpanded] = useState(false);
    const { user, loggedIn, token } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const breakpoint = useBreakpoint();

    const toggleDropdown = (title: string) => {
        setShow(prev => ({
            ...prev,
            [title]: !show[title],
        }));
    };

    const hideDropdown = (title: string) => {
        setShow(prev => ({
            ...prev,
            [title]: false,
        }));
    };

    const headerLinks: INavLinkType[] = [
        {
            title: "Standings / Schedules",
            location: "/tournaments",
        },
        {
            title: "Rules",
            location: "/rules",
        },
        {
            title: "Fields",
            location: "/fields",
        },
        {
            title: "Discount Lodging Options",
            location: "/discount-lodging",
        },
        {
            title: "Contact Us",
            location: "/contact-us",
        },
        {
            title: "Register",
            location: "#",
            children: [
                {
                    title: "Tournament Registration",
                    location: "/register/tournament",
                },
                {
                    title: "Coach Registration",
                    location: "/register/coach",
                },
            ],
        },
        {
            title: "Coach's Clubhouse",
            location: "/coachs-clubhouse",
            requireAuth: true,
            authLevels: AUTH_LEVELS.clubhouse,
        },
        {
            title: "Tournament Management",
            location: "/tournament-management",
            requireAuth: true,
            authLevels: AUTH_LEVELS.tourneymanagement,
        },
        {
            title: "Site Management",
            location: "/site-management",
            requireAuth: true,
            authLevels: AUTH_LEVELS.sitemanagement,
        },
    ];

    const buildChildren = (children: INavLinkType[]): React.ReactNode => {
        return children.map(child => (
            <NavDropdown.Item
                as={Link}
                to={child.location}
                key={child.title}
                onClick={
                    isXOrSmaller("md", breakpoint) ? toggleNavbar : () => false
                }
            >
                {child.title}
            </NavDropdown.Item>
        ));
    };

    const toggleNavbar = () => setNavBarExpanded(!navBarExpanded);

    const buildLinks = (links: INavLinkType[]) => {
        return links.map(
            (link: INavLinkType) =>
                (!link.requireAuth ||
                    (link.requireAuth &&
                        loggedIn &&
                        user &&
                        token &&
                        validateToken(token) &&
                        link.authLevels?.includes(user.userLevel))) && (
                    <Fragment key={link.title}>
                        {link.children ? (
                            <NavDropdown
                                title={link.title}
                                show={show[link.title]}
                                onClick={() => toggleDropdown(link.title)}
                                onMouseLeave={() => hideDropdown(link.title)}
                            >
                                {buildChildren(link.children)}
                            </NavDropdown>
                        ) : (
                            <Nav.Item>
                                <NavLink
                                    to={link.location}
                                    className="nav-link"
                                    role="headerNavLink"
                                    onClick={
                                        isXOrSmaller("md", breakpoint)
                                            ? toggleNavbar
                                            : () => false
                                    }
                                >
                                    {link.title}
                                </NavLink>
                            </Nav.Item>
                        )}
                    </Fragment>
                ),
        );
    };

    return (
        <div id="header" className={classNames("noPrint")}>
            <Container fluid="lg" className="header" data-testid="header">
                <Navbar expand="lg" className="nav" expanded={navBarExpanded}>
                    <NavLink className="nav-item" to="/">
                        <img
                            src={logo}
                            alt="WisonsinBaseballTournaments.com Logo"
                        />
                    </NavLink>
                    <Navbar.Toggle
                        aria-controls="basic-navbar-nav"
                        onClick={toggleNavbar}
                    />
                    <Navbar.Collapse>
                        <Nav>
                            {navBarExpanded && (
                                <>
                                    <Nav.Item>
                                        <NavLink
                                            to="/"
                                            className="nav-link"
                                            role="headerNavLink"
                                            onClick={
                                                isXOrSmaller("md", breakpoint)
                                                    ? toggleNavbar
                                                    : () => false
                                            }
                                        >
                                            Home
                                        </NavLink>
                                    </Nav.Item>
                                </>
                            )}
                            {buildLinks(headerLinks)}
                            {navBarExpanded && (
                                <>
                                    <hr />
                                    {buildLinks(topBarLinks)}
                                    {user ? (
                                        <>
                                            <Nav.Item>
                                                <NavLink
                                                    to="/profile"
                                                    className="nav-link"
                                                    role="headerNavLink"
                                                    onClick={
                                                        isXOrSmaller(
                                                            "md",
                                                            breakpoint,
                                                        )
                                                            ? toggleNavbar
                                                            : () => false
                                                    }
                                                >
                                                    My Profile
                                                </NavLink>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Link
                                                    to="#"
                                                    className="nav-link"
                                                    role="headerNavLink"
                                                    onClick={() => {
                                                        toggleNavbar();
                                                        dispatch(
                                                            authActions.logout(),
                                                        );
                                                        navigate("/");
                                                    }}
                                                >
                                                    Logout
                                                </Link>
                                            </Nav.Item>
                                        </>
                                    ) : (
                                        <Nav.Item>
                                            <NavLink
                                                to="/login"
                                                className={"nav-link login"}
                                                role="login-link"
                                                onClick={
                                                    isXOrSmaller(
                                                        "md",
                                                        breakpoint,
                                                    )
                                                        ? toggleNavbar
                                                        : () => false
                                                }
                                            >
                                                Login
                                            </NavLink>
                                        </Nav.Item>
                                    )}
                                </>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Navbar>
            </Container>
        </div>
    );
};

export default Header;
